import type { CancelTokenSource } from "axios"
import type { ApiResponse } from "@/src/types/apiResponse"
import type { GridSortModel, GridFilterItem } from "@mui/x-data-grid"

import useSWR from "swr"
import axios from "axios"
import { useMemo, useCallback } from "react"
import { apiUrls, fetcher, httpClient as axiosInstance } from "@/src/api"

import { swrOptions, getTableFilterString } from "./leads"

// Raw API response shape (fetcher returns res.data)
type MandatesListApiResponse = {
    data: {
        content: any[]
        totalElements: number | undefined
        jobCountByStatus: { [key: string]: number }
    }
}

/**
 * Build the SWR cache key for the mandates list (same as useGetMandates uses).
 */
export function getMandatesListKey(
    page: number,
    size: number,
    sortModel: GridSortModel,
    search: string,
    filterOptions: GridFilterItem[],
    extraFilters: MandateTableFilters
): string {
    const url = apiUrls.MANDATES_LIST()
    const filterString = getTableFilterString(sortModel, search, filterOptions, extraFilters)?.replace(
        "&status=ALL",
        ""
    )
    return `${url}?page=${page}&size=${size}${filterString ?? ""}`
}

/**
 * Directly fetch the jobs list from the API (e.g. after duplicate so list + cache stay in sync).
 * Returns the raw API response in the same shape as the SWR fetcher.
 */
export async function fetchMandatesList(
    page: number,
    size: number,
    sortModel: GridSortModel,
    search: string,
    filterOptions: GridFilterItem[],
    extraFilters: MandateTableFilters
): Promise<MandatesListApiResponse> {
    const url = apiUrls.MANDATES_LIST()
    const filterString = getTableFilterString(sortModel, search, filterOptions, extraFilters)?.replace(
        "&status=ALL",
        ""
    )
    const query = `?page=${page}&size=${size}${filterString ?? ""}`
    const res = await axiosInstance.get(`${url}${query}`)
    return res.data
}
import { convertEmptyStringsToNull } from "../utils/helper"
import {
    mapJobPostingToForm,
    resolveClientId,
    resolveClientIdForApiPayload,
} from "../features/jobPosting/lib/jobPostingMappers"

import type { MandatesModel, MandateTableFilters } from "../types/mandatesModel"
import type { FormSchemaJobPostingType } from "../features/jobPosting/schemas/jobPostingFormSchema"
import { shouldOmitHybridSchedulerRoundFromPersist } from "../features/jobPosting/jobPostingForm/interviewSetupStep/interviewSetupConstants"

// ----------------------------------------------------------------------

type MandatesData = {
    data: {
        content: MandatesModel[]
        totalElements: number | undefined
        jobCountByStatus: { [key: string]: number }
    }
}

function mergeTabCounts(
    unfilteredTabCounts: { [key: string]: number },
    currentStatus: string | undefined,
    search: string,
    filteredTotalElements: number | undefined
): { [key: string]: number } {
    const mergedTabCounts = { ...unfilteredTabCounts }

    if (search && currentStatus && filteredTotalElements !== undefined) {
        mergedTabCounts[currentStatus] = filteredTotalElements
    }

    return mergedTabCounts
}

export function useGetMandates(
    page: number,
    size: number,
    sortModel: GridSortModel,
    search: string,
    filterOptions: GridFilterItem[],
    extraFilters: MandateTableFilters,
    keepPreviousData?: boolean,
    enabled: boolean = true
) {
    const url = useMemo(() => apiUrls.MANDATES_LIST(), [])

    const filterString = useMemo(
        () =>
            getTableFilterString(sortModel, search, filterOptions, extraFilters)?.replace(
                "&status=ALL", // ALL doesn't exist in BE, it's the same as no filter
                ""
            ),
        [sortModel, search, filterOptions, extraFilters]
    )

    const tabCountsFilterString = useMemo(() => {
        const extraFiltersWithoutStatus = { ...extraFilters, status: undefined }
        const filterString = getTableFilterString(sortModel, "", filterOptions, extraFiltersWithoutStatus)
        return filterString?.replace("&status=ALL", "")
    }, [sortModel, filterOptions, extraFilters])

    const swrKey = useMemo(
        () => (enabled ? `${url}?page=${page}&size=${size}${filterString}` : null),
        [enabled, url, page, size, filterString]
    )

    const tabCountsSwrKey = useMemo(
        () => (enabled ? `${url}?page=0&size=1${tabCountsFilterString}` : null),
        [enabled, url, tabCountsFilterString]
    )

    const { data, isLoading, error, isValidating, mutate } = useSWR<MandatesData>(swrKey, fetcher, {
        ...swrOptions,
        keepPreviousData,
        revalidateOnFocus: false, // Prevent revalidation on window focus
        revalidateOnReconnect: false, // Prevent revalidation on network reconnect
        revalidateIfStale: false, // Only revalidate when explicitly requested
    })

    // Fetch tab counts separately without search query
    const { data: tabCountsData, mutate: mutateTabCounts } = useSWR<MandatesData>(tabCountsSwrKey, fetcher, {
        ...swrOptions,
        revalidateOnFocus: false,
        revalidateOnReconnect: false,
        revalidateIfStale: false,
    })

    // Combined mutate function that invalidates both queries
    const mutateAll = useCallback(async () => {
        await Promise.all([mutate(), mutateTabCounts()])
    }, [mutate, mutateTabCounts])

    const memoizedValue = useMemo(() => {
        const unfilteredTabCounts = tabCountsData?.data?.jobCountByStatus ?? {}
        const currentStatus = extraFilters.status?.[0]
        const mergedTabCounts = mergeTabCounts(unfilteredTabCounts, currentStatus, search, data?.data?.totalElements)

        return {
            mandates: data?.data?.content?.map(mapApiToUIMandate) ?? [],
            mandatesCount: data?.data?.totalElements,
            tabData: mergedTabCounts,
            mandatesLoading: isLoading || isValidating,
            mandatesError: error,
            mandatesEmpty: !isLoading && !data?.data?.content?.length,
            mandatesMutate: mutateAll,
            mandatesListKey: swrKey,
        }
    }, [data, tabCountsData, error, isLoading, isValidating, mutateAll, search, extraFilters.status, swrKey])

    return memoizedValue
}

export function useGetDropdownMandates(page: number, size: number, search: string) {
    const url = apiUrls.MANDATES_DROPDOWN()

    const { data, isLoading, error, mutate } = useSWR<MandatesData>(
        `${url}?page=${page}&size=${size}${search ? `&search=${search}` : ""}`,
        fetcher,
        { ...swrOptions, keepPreviousData: true }
    )

    const memoizedValue = useMemo(
        () => ({
            mandates: data?.data?.content?.map(mapApiToUIMandate) ?? [],
            mandatesCount: data?.data?.totalElements,
            mandatesLoading: isLoading,
            mandatesError: error,
            mandatesMutate: mutate,
        }),
        [data, error, isLoading, mutate]
    )

    return memoizedValue
}

// ----------------------------------------------------------------------

type ClientData = { data: MandatesModel }

export function useGetJobs(jobId: string | undefined) {
    const url = jobId ? apiUrls.MANDATE_DETAILS(jobId) : undefined

    const { data, isLoading, error, isValidating, mutate } = useSWR<ClientData>(url, fetcher, swrOptions)

    const memoizedValue = useMemo(
        () => ({
            jobs: data?.data ? mapApiToUIMandate(data?.data) : undefined,
            jobsLoading: isLoading || isValidating,
            jobsError: error,
            jobsMutate: mutate,
        }),
        [data, error, isLoading, isValidating, mutate]
    )

    return memoizedValue
}

export async function getMandate(mandateId: string): Promise<ApiResponse<MandatesModel>> {
    try {
        const resp = await axiosInstance.get(apiUrls.MANDATE_DETAILS(mandateId))
        const mappedMandate = resp?.data?.data ? mapApiToUIMandate(resp?.data?.data) : undefined
        if (!mappedMandate) {
            return {
                status: "error",
                errorMessage: "Mandate not found",
            }
        }
        return {
            status: "success",
            data: mappedMandate,
        }
    } catch (e: any) {
        console.log(e)
        return {
            status: "error",
            errorMessage: e.status,
        }
    }
}

export function useGetMandatePublic(mandateId: string) {
    const url = mandateId ? apiUrls.MANDATE_DETAILS_PUBLIC(mandateId) : ""

    const { data, isLoading, error, isValidating } = useSWR<ClientData>(
        url,
        fetcher,
        // (a, b) => {
        //     return axiosInstance.get(a, { headers: { Authorization: null } })
        // },
        swrOptions
    )

    const memoizedValue = useMemo(
        () => ({
            mandates: data?.data ? mapApiToUIMandate(data?.data) : undefined,
            mandatesLoading: isLoading,
            mandatesError: error,
            mandatesValidating: isValidating,
        }),
        [data, error, isLoading, isValidating]
    )

    return memoizedValue
}

export const mapApiToUIMandate = (item: any): MandatesModel => {
    return {
        ...item,
        client: item?.client
            ? {
                  id: item.client.id,
                  name: item.client.name,
                  label: item.client.name,
                  value: item.client.id,
                  logoUrl: item.client.logoUrl,
              }
            : undefined,
        jobDescriptionPrompt: item?.jobDescription ?? "",
        jobDescription: item?.jobDescription,
        jdFileLink: item?.jobDescriptionUrl ?? item?.jdFileLink ?? null,
        displayName: item?.displayName,
        jobTitle: item?.title ?? item?.displayName ?? item?.jobTitle,
        jobType: item?.workType ?? item?.jobType,
        rounds:
            item?.rounds?.map((round: any) => ({
                ...round,
                roundName: round?.name ?? round?.roundName,
                aiEnabled: round?.ai ?? round?.aiEnabled,
            })) ?? [],
        createdBy: item?.createdBy ? { ...item.createdBy, label: item.createdBy.name } : undefined,
        createdDate: item?.createdAt ?? item?.createdDate,
        lastModifiedDate: item?.lastModifiedAt ?? item?.lastModifiedDate,
    }
}

function getMandateClientFallbackSources(): Array<{ clientId?: string | null; client?: { id?: string; value?: string } | null } | null | undefined> {
    try {
        // Lazy import avoids circular dependency: mandates.slice -> mandates.ts -> store -> mandates.slice
        const { useMandatesStore } = require("@/src/store") as typeof import("@/src/store")
        const state = useMandatesStore.getState()
        return [state.currentMandate, state.formData]
    } catch {
        return []
    }
}

const mapFormToApiMandate = (item: FormSchemaJobPostingType) => {
    const basePayload = convertEmptyStringsToNull({
        jobDescription: item.jobDescriptionPrompt,
        jdFileLink: item.jdFileLink,
        clientId: resolveClientIdForApiPayload(item, ...getMandateClientFallbackSources()),
        title: item.title,
        jobCity: item.jobCity,
        jobArea: item.jobArea,
        experienceRequired: item.experienceRequired,
        experiencedCandidateDefinition: item.experiencedCandidateDefinition,
        fresherCandidateDefinition: item.fresherCandidateDefinition,
        workType: item.workType,
        workMode: item.workMode,
        workScheduleAndShiftDetails: item.workScheduleAndShiftDetails,
        experiencedCompensationDetails: item.experiencedCompensationDetails,
        freshersCompensationDetails: item.freshersCompensationDetails,
        additionalDetails: item.additionalDetails,
        interviewProcess: item.interviewProcess,
        status: item.status,
        agentFriendlyJobDescription: item.agentFriendlyJobDescription,
        roundCost: 1,
    })

    const extraPayload: Record<string, any> = {}

    const hasCandidateFaqPairs = item.candidateFaqPairs && item.candidateFaqPairs.length > 0
    const hasCandidateFaqFile = !!item.candidateFaqFileUrl
    if (hasCandidateFaqPairs || hasCandidateFaqFile) {
        extraPayload.candidateFaq = {
            candidateFaqUrl: item.candidateFaqFileUrl ?? null,
            candidateFaqs: item.candidateFaqPairs ?? [],
        }
    }

    // Include rounds data if available (for PUT requests that need round info)
    // Only include rounds if they have been explicitly provided and have valid data
    // This prevents sending default/generated rounds when they shouldn't be sent
    if (item.rounds && item.rounds.length > 0) {
        // Filter out rounds that are empty or have default values that shouldn't be sent
        const validRounds = item.rounds
            .filter((round) => {
                // Only include rounds that have been explicitly configured (have roundName and roundType)
                if (
                    !(
                        round.roundName &&
                        round.roundName.trim() !== "" &&
                        round.roundType &&
                        round.roundType.trim() !== ""
                    )
                ) {
                    return false
                }

                return !shouldOmitHybridSchedulerRoundFromPersist(round, {
                    completeRoundsInSingleCall: item.completeRoundsInSingleCall,
                    rounds: item.rounds,
                })
            })
            .map((round) => {
                const isInterviewSchedulingRound = (round as any).taskType === "INTERVIEW_SCHEDULING"

                return {
                    roundName: round.roundName,
                    roundDescription: round.roundBriefDescription, // Map roundBriefDescription to roundDescription for API
                    aiEnabled: isInterviewSchedulingRound ? true : round.aiEnabled,
                    roundType: round.roundType,
                    roundNumber: round.roundNumber,
                }
            })

        // Only include rounds array if there are valid rounds
        if (validRounds.length > 0) {
            return {
                ...basePayload,
                ...extraPayload,
                rounds: validRounds,
            }
        }
    }

    return { ...basePayload, ...extraPayload }
}

export const editMandateForm = async (
    payload: FormSchemaJobPostingType,
    id: string
): Promise<ApiResponse<MandatesModel>> => {
    try {
        const resp = await axiosInstance.put(`${apiUrls.MANDATE_UPDATE()}/${id}`, mapFormToApiMandate(payload))
        return {
            status: "success",
            data: resp?.data?.data ? mapApiToUIMandate(resp.data.data) : undefined,
        }
    } catch (e: any) {
        console.log(e)
        return { status: "error", errorMessage: e.status }
    }
}

export const createJobDraft = async (payload: FormSchemaJobPostingType): Promise<ApiResponse<MandatesModel>> => {
    try {
        const resp = await axiosInstance.post(apiUrls.MANDATE_DRAFT(), mapFormToApiMandate(payload))
        return {
            status: "success",
            data: resp?.data?.data ? mapApiToUIMandate(resp.data.data) : undefined,
        }
    } catch (e: any) {
        console.log(e)
        return { status: "error", errorMessage: e.status }
    }
}

/** POST .../jobs/:id/activate — response body includes full job (id, rounds, etc.) for post-activate UX. */
export const activateDraftMandate = async (id: string): Promise<ApiResponse<MandatesModel>> => {
    try {
        const resp = await axiosInstance.post(apiUrls.MANDATE_ACTIVATE(id))
        return { status: "success", data: resp?.data?.data }
    } catch (e: any) {
        console.log(e)
        return { status: "error", errorMessage: e.status }
    }
}

export const enableAiForMandate = async (
    mandateId: string,
    agentId: string,
    triggerForExisting: boolean
): Promise<ApiResponse<void>> => {
    try {
        const resp = await axiosInstance.put(apiUrls.MANDATE_ENABLE_AI(mandateId), {
            triggerAiScreeningForExistingLeads: true,
            agentId,
        })
        return { status: "success", data: resp?.data?.data }
    } catch (e: any) {
        console.log(e)
        return { status: "error", errorMessage: e.status }
    }
}

export function getMandateFromJd(
    jd: string,
    jdLink: string | null,
    initialFormState: FormSchemaJobPostingType
): [Promise<ApiResponse<FormSchemaJobPostingType>>, CancelTokenSource] {
    const cancelToken = axios.CancelToken.source()

    const promise = axiosInstance
        .post(
            apiUrls.AI_EXTRACT_JOB_DETAILS(),
            convertEmptyStringsToNull({
                jobDescription: jd,
                jobDescriptionUrl: jdLink,
            }),
            {
                cancelToken: cancelToken.token,
            }
        )
        .then((resp): ApiResponse<FormSchemaJobPostingType> => {
            const mappedExtractedFields = mapJobPostingToForm(resp.data?.data)

            return {
                status: "success",
                data: {
                    ...initialFormState,
                    ...mappedExtractedFields,
                    jobDescriptionPrompt: jd,
                    clientId: resolveClientId(mappedExtractedFields) || resolveClientId(initialFormState),
                },
            }
        })
        .catch((e): ApiResponse<FormSchemaJobPostingType> => {
            console.log(e)
            return {
                status: "error",
                errorMessage: e.status,
            }
        })

    return [promise, cancelToken]
}

export const getJdFromText = async (text: string): Promise<ApiResponse<string>> => {
    try {
        const resp = await axiosInstance.post(apiUrls.MANDATE_GENERATE_JD_TEXT(), {
            jobDescriptionText: text,
        })
        return {
            status: "success",
            data: resp?.data?.data?.jobDescription,
        }
    } catch (e: any) {
        console.log(e)
        return {
            status: "error",
            errorMessage: e.status,
        }
    }
}

export const generateJdFromFile = async (
    file: File
): Promise<ApiResponse<{ jobDescription: string; jobDescriptionLink: string }>> => {
    const headers = {
        "Content-Type": "application/octet-stream",
    }

    try {
        const formData = new FormData()
        formData.append("file", file)
        const resp = await axiosInstance.post(apiUrls.MANDATE_GENERATE_JD_FILE(), formData, { headers })
        return {
            status: "success",
            data: resp?.data?.data,
        }
    } catch (e: any) {
        console.log(e)
        return {
            status: "error",
            errorMessage: e.status,
        }
    }
}

export const generateDummyCandidate = async (
    jobId: string,
    currentRound: { id: string }
): Promise<ApiResponse<string>> => {
    try {
        const resp = await axiosInstance.post(`${apiUrls.MANDATE_GENERATE_DUMMY_CANDIDATE()}?jobId=${jobId}`)
        const rounds: { roundId: string; id: string }[] = resp?.data?.data
        const lead = rounds.find((round) => round.roundId === currentRound.id)?.id
        if (lead) {
            return {
                status: "success",
                data: lead,
            }
        }
        return {
            status: "error",
            data: undefined,
        }
    } catch (e: any) {
        console.log(e)
        return {
            status: "error",
            errorMessage: e.status,
        }
    }
}

export const deleteRoundById = async (roundId: string): Promise<ApiResponse<void>> => {
    try {
        await axiosInstance.delete(apiUrls.MANDATE_DELETE_ROUND(roundId))
        return {
            status: "success",
            data: undefined,
        }
    } catch (e: any) {
        console.log(e)
        return {
            status: "error",
            errorMessage: e.status,
        }
    }
}

export const deactivateMandate = async (mandateId: string): Promise<ApiResponse<void>> => {
    try {
        const resp = await axiosInstance.post(apiUrls.MANDATE_DEACTIVATE(mandateId))
        return {
            status: "success",
            data: resp?.data?.data,
        }
    } catch (e: any) {
        console.log(e)
        return { status: "error", errorMessage: e.status, data: undefined }
    }
}

export const duplicateMandate = async (mandateId: string): Promise<ApiResponse<void>> => {
    try {
        const resp = await axiosInstance.post(apiUrls.MANDATE_DUPLICATE(mandateId))
        return { status: "success", data: resp?.data?.data }
    } catch (e: any) {
        console.log(e)
        return { status: "error", errorMessage: e.status, data: undefined }
    }
}
